import { Switch } from '@/common/ui/switch'
import { Input } from '@/common/ui/input'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GetGeneralSettingResponse } from '@/modules/client-setting/type'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { useQueryClient } from '@tanstack/react-query'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'
import useHasPermission from '@/common/hooks/useHasPermission'
import { PermissionKeys } from '@/modules/manage-member/mappers'

interface MaxLoginAttemptsProps {
	data: GetGeneralSettingResponse
}

export function MaxLoginAttempts({ data }: MaxLoginAttemptsProps) {
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const [maxAttempts, setMaxAttempts] = useState(
		data?.maxLoginAttempts && data.maxLoginAttempts > 0
			? data.maxLoginAttempts
			: 5,
	)
	const { mutateAsync: updateGeneralSetting } =
		useMutationUpdateGeneralSetting()
	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const isEnabled = (data?.maxLoginAttempts ?? 0) > 0

	const handleToggleChange = async (value: boolean) => {
		if (!hasWritePermission) return

		setIsLoading(true)
		try {
			await updateGeneralSetting({
				payload: {
					...data,
					maxLoginAttempts: value ? maxAttempts : 0,
				},
			})
			toast.success('อัพเดทการตั้งค่าจำกัดการล็อกอินสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	const handleAttemptsChange = async (value: number | null) => {
		if (!hasWritePermission || !isEnabled || value === null || value < 1) return

		setMaxAttempts(value)
		setIsLoading(true)
		try {
			await updateGeneralSetting({
				payload: {
					...data,
					maxLoginAttempts: value,
				},
			})
			toast.success('อัพเดทจำนวนครั้งการล็อกอินสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="flex items-center gap-3">
			{isEnabled && (
				<div className="flex items-center gap-2">
					<Input
						type="number"
						min={1}
						value={maxAttempts}
						onNumberChange={handleAttemptsChange}
						disabled={isLoading || !hasWritePermission}
						className="w-20"
					/>
					<span className="text-sm text-muted-foreground">ครั้ง</span>
				</div>
			)}
			<Switch
				checked={isEnabled}
				onCheckedChange={handleToggleChange}
				disabled={isLoading || !hasWritePermission}
			/>
		</div>
	)
}
